'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const floatingCards = [
{
  id: 'card-shopify',
  title: 'Luxe Store',
  platform: 'Shopify',
  price: '$79',
  rating: 4.9,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dcc0d530-1766986393247.png",
  alt: 'Luxe Store Shopify template showing an elegant e-commerce layout with product grid',
  className: 'animate-float-a',
  style: { top: '8%', right: '4%', width: '220px' }
},
{
  id: 'card-wp',
  title: 'Editorial Blog',
  platform: 'WordPress',
  price: '$49',
  rating: 4.7,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12fa02131-1767849577516.png",
  alt: 'Editorial Blog WordPress template with clean typography and article layout',
  className: 'animate-float-b',
  style: { top: '40%', right: '-2%', width: '200px' }
},
{
  id: 'card-html',
  title: 'Agency Pro',
  platform: 'HTML/CSS',
  price: '$39',
  rating: 4.8,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13212e49a-1768585040713.png",
  alt: 'Agency Pro HTML template featuring a bold hero section and services grid',
  className: 'animate-float-c',
  style: { bottom: '10%', right: '8%', width: '190px' }
}];


const platformColors: Record<string, string> = {
  Shopify: '#96BF48',
  WordPress: '#21759B',
  'HTML/CSS': '#E8633A',
  WooCommerce: '#7F54B3'
};

interface HomepageStats {
  premiumTemplates: number;
  happyCustomers: number;
  platformsSupported: number;
  averageRating: string;
}

interface HeroSectionProps {
  stats: HomepageStats;
  monthlySalesCount: number;
  growthPercent: number | null;
  showGrowth: boolean;
  showSalesWidget: boolean;
}

export default function HeroSection({ stats, monthlySalesCount, growthPercent, showGrowth, showSalesWidget }: HeroSectionProps) {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const els = heroRef.current?.querySelectorAll('.hero-anim');
    els?.forEach((el, i) => {
      setTimeout(() => {
        (el as HTMLElement).style.opacity = '1';
        (el as HTMLElement).style.transform = 'translateY(0)';
      }, i * 120);
    });
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden pt-16"
      style={{ background: 'var(--bg-primary)' }}>
      
      {/* Background decorations */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true">
        
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(232,99,58,0.3) 0%, rgba(232,99,58,0.05) 60%, transparent 100%)'
          }} />
        
        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-15 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(33,117,155,0.3) 0%, transparent 70%)'
          }} />
        
        {/* Grid pattern */}
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.025]"
          xmlns="http://www.w3.org/2000/svg">
          
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="max-w-8xl mx-auto px-6 w-full py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Copy */}
          <div>
            {/* Badge */}
            <div
              className="hero-anim inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-8"
              style={{
                opacity: 0,
                transform: 'translateY(20px)',
                transition: 'all 0.7s cubic-bezier(0.16,1,0.3,1)',
                background: 'var(--accent-light)',
                color: 'var(--accent)',
                border: '1px solid rgba(232,99,58,0.2)'
              }}>
              
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse-glow"
                style={{ background: 'var(--accent)' }} />
              
              {stats.premiumTemplates.toLocaleString()}+ Premium Templates Available
            </div>

            {/* Headline */}
            <h1
              className="hero-anim font-display font-light leading-[0.92] tracking-tight mb-6"
              style={{
                fontSize: 'clamp(3.2rem, 7vw, 7rem)',
                opacity: 0,
                transform: 'translateY(28px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
                color: 'var(--text-primary)'
              }}>
              
              Launch your{' '}
              <span
                className="italic"
                style={{ color: 'var(--accent)' }}>
                
                website
              </span>
              <br />
              in days,{' '}
              <span className="italic">not months.</span>
            </h1>

            <p
              className="hero-anim text-lg leading-relaxed mb-10 max-w-xl"
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
                color: 'var(--text-secondary)'
              }}>
              
              Browse {stats.premiumTemplates.toLocaleString()}+ professionally designed templates for Shopify, WordPress, WooCommerce, and HTML. Buy once, download forever.*
            </p>

            {/* CTA row */}
            <div
              className="hero-anim flex flex-wrap items-center gap-4 mb-12"
              style={{
                opacity: 0,
                transform: 'translateY(20px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)'
              }}>
              
              <Link href="/templates" className="btn-primary text-base px-7 py-3.5">
                Browse Templates
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
              <Link href="/sign-up-login" className="btn-secondary text-base px-7 py-3.5">
                Create Free Account
              </Link>
            </div>

            {/* Trust signals */}
            <div
              className="hero-anim flex flex-wrap items-center gap-6"
              style={{
                opacity: 0,
                transform: 'translateY(16px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)'
              }}>
              
              {[
              { icon: 'ShieldCheckIcon', label: 'Secure Checkout' },
              { icon: 'ArrowDownTrayIcon', label: 'Lifetime Downloads' },
              { icon: 'KeyIcon', label: 'License Key Included' }].
              map((item) =>
              <div key={item.label} className="flex items-center gap-2">
                  <Icon
                  name={item.icon as 'ShieldCheckIcon'}
                  size={15}
                  className="flex-shrink-0"
                  style={{ color: 'var(--accent)' } as React.CSSProperties} />
                
                  <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                    {item.label}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Floating cards */}
          <div className="relative hidden lg:block h-[580px]">
            {floatingCards.map((card) =>
            <div
              key={card.id}
              className={`absolute ${card.className} template-card rounded-2xl overflow-hidden cursor-pointer`}
              style={card.style}>
              
                <div className="card-img-wrap h-28">
                  <AppImage
                  src={card.image}
                  alt={card.alt}
                  fill
                  className="object-cover" />
                
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span
                    className="badge"
                    style={{
                      background: `${platformColors[card.platform]}18`,
                      color: platformColors[card.platform]
                    }}>
                    
                      {card.platform}
                    </span>
                    <span className="price-tag text-sm">{card.price}</span>
                  </div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {card.title}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Icon name="StarIcon" size={11} variant="solid" className="star-filled" />
                    <span className="text-2xs font-medium" style={{ color: 'var(--text-muted)' }}>
                      {card.rating}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Center decorative element */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-10 animate-spin-slow"
              style={{
                border: '1px dashed var(--accent)'
              }}
              aria-hidden="true" />
            
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full opacity-5"
              style={{
                background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
              }}
              aria-hidden="true" />
            

            {/* Stats card - only shown when 20+ sales achieved this month */}
            {showSalesWidget && (
              <div
                className="absolute top-[28%] left-[5%] rounded-2xl p-4 shadow-lg"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  width: '160px'
                }}>
                
                <p className="text-2xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>
                  This Month
                </p>
                <p className="font-display text-3xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {monthlySalesCount.toLocaleString()}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>templates sold</p>
                {showGrowth && growthPercent !== null && (
                  <div className="flex items-center gap-1 mt-2">
                    <Icon 
                      name={growthPercent >= 0 ? "ArrowTrendingUpIcon" : "ArrowTrendingDownIcon"} 
                      size={13} 
                      style={{ color: growthPercent >= 0 ? '#22C55E' : '#EF4444' } as React.CSSProperties} 
                    />
                    <span 
                      className="text-2xs font-semibold" 
                      style={{ color: growthPercent >= 0 ? '#22C55E' : '#EF4444' }}
                    >
                      {growthPercent >= 0 ? '+' : ''}{Math.round(growthPercent)}% vs last month
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce"
        aria-hidden="true">
        
        <span className="text-2xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
          Scroll
        </span>
        <div
          className="w-px h-8"
          style={{ background: 'linear-gradient(to bottom, var(--text-muted), transparent)' }} />
        
      </div>
    </section>
  );
}